import { CaptainNavigation2DMapView } from './CaptainNavigation2DMapView.js';
import { CaptainNavigationSideView } from './CaptainNavigationSideView.js';
import type { NavGatewayEntry, SectionAxis } from './navSection.js';
import type { GameSnapshot, RoomState } from '../../shared/types.js';
import type { AggregatedConnection, TileJsonNode } from '../../shared/maps/tileJson.js';

// ---------------------------------------------------------------------------
// The navigation chart as two linked views: TOP (plan, the original chart) and SIDE (elevation).
// Wide enough -> both are shown together; otherwise a toggle picks one. A tapped marker and the shared waypoint
// are the same object in both, so selecting in one shows in the other.
// ---------------------------------------------------------------------------

type NodeEntry = { node: TileJsonNode; tileId: string; chunkRegion: string; biome: string; tempC: number; tileKind?: string };
type ViewMode = 'top' | 'side' | 'both';

/** Side by side when the chart is at least this wide and wider than tall. */
const SPLIT_ROW_MIN_WIDTH_PX = 640;
/** Stacked (top over side) when each pane can still be at least this tall. */
const STACK_PANE_MIN_HEIGHT_PX = 250;
const BAR_HEIGHT_PX = 36;
const MODE_TITLES: Record<ViewMode, string> = {
  top: 'Plan view: position on the chart',
  side: 'Side view: depth and what lies above and below',
  both: 'Plan and side view together'
};
const MODE_STORAGE_KEY = 'aegis7.nav.viewMode';

export type NavViewState = {
  top: ReturnType<CaptainNavigation2DMapView['getViewState']>;
  side: ReturnType<CaptainNavigationSideView['getViewState']>;
  mode: ViewMode;
};

export class CaptainNavigation25DView {
  /** Called when the player sets or clears the shared waypoint (world coords in map space, + depth when known). */
  public onWaypointChange: ((pos: { worldX: number; worldZ: number; depth?: number } | null) => void) | null = null;

  private readonly root: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly panes: HTMLElement;
  private readonly topPane: HTMLElement;
  private readonly sidePane: HTMLElement;
  private readonly modeButtons = new Map<ViewMode, HTMLButtonElement>();
  private readonly topView: CaptainNavigation2DMapView;
  private readonly sideView: CaptainNavigationSideView;
  private readonly resizeObserver: ResizeObserver;
  private mode: ViewMode = 'both';
  private effective: ViewMode = 'both';

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'nav-25d';
    this.root.style.cssText = 'position:absolute;inset:0;display:flex;flex-direction:column;';
    container.style.position = 'relative';

    this.bar = document.createElement('div');
    this.bar.className = 'nav-25d-bar';
    this.panes = document.createElement('div');
    this.panes.className = 'nav-25d-panes';
    this.topPane = document.createElement('div');
    this.topPane.className = 'nav-25d-pane nav-25d-top';
    this.sidePane = document.createElement('div');
    this.sidePane.className = 'nav-25d-pane nav-25d-side';
    this.panes.append(this.topPane, this.sidePane);
    this.root.append(this.bar, this.panes);
    container.appendChild(this.root);

    this.buildBar();
    this.mode = this.loadMode();

    this.topView = new CaptainNavigation2DMapView(this.topPane);
    this.sideView = new CaptainNavigationSideView(this.sidePane, {
      getWaypoint: () => this.topView.getWaypoint(),
      getMarker: () => this.topView.getMarker(),
      setMarker: (marker) => {
        this.topView.setMarkerExternal(marker);
        this.sideView.markDirty();
      },
      moveWaypoint: (x, z, depth) => {
        this.topView.applyWaypoint(x, z, depth);
        this.sideView.markDirty();
      }
    });

    // The plan view owns waypoint state and talks to the server; mirror its changes into the side view.
    this.topView.onWaypointChange = (pos) => {
      this.onWaypointChange?.(pos);
      this.sideView.markDirty();
    };
    this.topView.onMarkerChange = () => this.sideView.markDirty();

    this.resizeObserver = new ResizeObserver(() => this.applyLayout());
    this.resizeObserver.observe(container);
    this.applyLayout();
  }

  // ---- layout -------------------------------------------------------------

  private buildBar(): void {
    const label = document.createElement('span');
    label.className = 'nav-25d-bar-label';
    label.textContent = 'VIEW';
    this.bar.appendChild(label);
    const names: Record<ViewMode, string> = { top: 'TOP', side: 'SIDE', both: 'BOTH' };
    for (const mode of ['top', 'side', 'both'] as const) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = names[mode];
      button.title = MODE_TITLES[mode];
      button.addEventListener('click', () => this.setMode(mode));
      this.modeButtons.set(mode, button);
      this.bar.appendChild(button);
    }
  }

  private loadMode(): ViewMode {
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      if (stored === 'top' || stored === 'side' || stored === 'both') return stored;
    } catch { /* storage unavailable */ }
    return 'both';
  }

  public setMode(mode: ViewMode): void {
    this.mode = mode;
    try { localStorage.setItem(MODE_STORAGE_KEY, mode); } catch { /* storage unavailable */ }
    this.applyLayout();
  }

  private applyLayout(): void {
    const w = this.root.clientWidth;
    const h = this.root.clientHeight - BAR_HEIGHT_PX;
    const row = w >= SPLIT_ROW_MIN_WIDTH_PX && w >= h;
    const canBoth = row || h >= STACK_PANE_MIN_HEIGHT_PX * 2;
    this.effective = this.mode === 'both' && !canBoth ? 'top' : this.mode;
    const both = this.effective === 'both';

    this.panes.style.cssText = `flex:1;min-height:0;display:flex;flex-direction:${row ? 'row' : 'column'};gap:2px;`;
    const paneStyle = 'position:relative;flex:1 1 0;min-width:0;min-height:0;overflow:hidden;';
    this.topPane.style.cssText = `${paneStyle}display:${both || this.effective === 'top' ? 'block' : 'none'};`;
    this.sidePane.style.cssText = `${paneStyle}display:${both || this.effective === 'side' ? 'block' : 'none'};`;
    // The panes' own constructors set position/overflow/background inline; restore what they need.
    this.topPane.style.background = '#050810';
    this.sidePane.style.background = '#050810';

    for (const [mode, button] of this.modeButtons) {
      button.classList.toggle('active', mode === this.effective);
      button.disabled = mode === 'both' && !canBoth;
      button.title = mode === 'both' && !canBoth ? 'Not enough room on this screen to show both' : MODE_TITLES[mode];
    }
  }

  // ---- data (fanned out to both views) --------------------------------------

  public setTileData(nodeEntries: NodeEntry[], connections: AggregatedConnection[], gateways: NavGatewayEntry[] = []): void {
    this.topView.setTileData(nodeEntries, connections);
    this.sideView.setTileData(nodeEntries, connections, gateways);
  }

  public setRevealedNodes(ids: Set<string>): void {
    this.topView.setRevealedNodes(ids);
    this.sideView.setRevealedNodes(ids);
  }

  public setRoomState(state: Pick<RoomState, 'mission' | 'missionObjectives' | 'activeMissionIndex'>): void {
    this.topView.setRoomState(state);
    this.sideView.setRoomState(state);
  }

  public setSnapshot(snapshot: GameSnapshot): void {
    this.topView.setSnapshot(snapshot);
    this.sideView.setSnapshot(snapshot);
  }

  // ---- controls -------------------------------------------------------------

  public hasWaypoint(): boolean {
    return this.topView.hasWaypoint();
  }

  public toggleWaypoint(): void {
    this.topView.toggleWaypoint();
    this.sideView.markDirty();
  }

  public recenter(): void {
    this.topView.recenter();
    this.sideView.recenter();
  }

  public setSectionAxis(axis: SectionAxis): void {
    this.sideView.setAxis(axis);
  }

  public getViewState(): NavViewState {
    return { top: this.topView.getViewState(), side: this.sideView.getViewState(), mode: this.mode };
  }

  public restoreViewState(state: NavViewState): void {
    this.topView.restoreViewState(state.top);
    this.sideView.restoreViewState(state.side);
    this.mode = state.mode;
    this.applyLayout();
  }

  public dispose(): void {
    this.resizeObserver.disconnect();
    this.topView.dispose();
    this.sideView.dispose();
    this.root.remove();
  }
}
